const LONG: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };

/**
 * How to pick the year for a date typed without one.
 * - "next": the next time that day comes around (start / end dates — "3/1"
 *   typed in October is next March).
 * - "nearest": whichever year puts it closest to today (the contract date —
 *   "9/28" typed on Oct 1 is three days ago, not next September).
 */
export type YearGuess = "next" | "nearest";

function guessYear(month: number, day: number, now: Date, guess: YearGuess): Date {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y = now.getFullYear();
  if (guess === "next") {
    const candidate = new Date(y, month - 1, day);
    return candidate < today ? new Date(y + 1, month - 1, day) : candidate;
  }
  const options = [y - 1, y, y + 1].map((yr) => new Date(yr, month - 1, day));
  const distance = (d: Date) => Math.abs(d.getTime() - today.getTime());
  return options.reduce((best, d) => (distance(d) < distance(best) ? d : best));
}

// Beyond M/D, only a written month plus a day is treated as a date. The
// browser parser also "reads" text like "spring 2027" as January 1, which
// must stay exactly as typed.
const MONTH_AND_DAY =
  /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{1,2}\b/i;

/**
 * Read a typed date, or null when the text isn't one. A date typed with no
 * year ("7/5", "7/5/", "7.5", "Jul 5", "July 5") gets its year from `guess`.
 * Native `new Date("Jul 5")` silently picks the year 2001 — that must never
 * reach a contract, so the browser parser is only trusted for its year when
 * the text actually contains one.
 */
export function parseTypedDate(
  raw: string,
  now: Date = new Date(),
  guess: YearGuess = "next"
): Date | null {
  const s = (raw || "").trim();
  if (!s) return null;

  const mdy = s.match(/^(\d{1,2})\s*[/.\-]\s*(\d{1,2})(?:\s*[/.\-]\s*(\d{2,4})?)?$/);
  if (mdy) {
    const month = parseInt(mdy[1], 10);
    const day = parseInt(mdy[2], 10);
    let d: Date;
    if (mdy[3]) {
      let year = parseInt(mdy[3], 10);
      if (year < 100) year += 2000;
      d = new Date(year, month - 1, day);
    } else {
      d = guessYear(month, day, now, guess);
    }
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (!MONTH_AND_DAY.test(s)) return null;
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  // "March 1", "Mar 1" — no year written, so the 2001 the parser invented
  // is replaced by the guessed year.
  const hasYear = /\d{4}|[,'’]\s*\d{2}\s*$/.test(s);
  return hasYear ? parsed : guessYear(parsed.getMonth() + 1, parsed.getDate(), now, guess);
}

// "May 17, 2026". Text that isn't a date is returned exactly as typed.
export function formatLongDate(
  raw: string,
  now: Date = new Date(),
  guess: YearGuess = "next"
): string {
  const s = (raw || "").trim();
  if (!s) return "";
  const d = parseTypedDate(s, now, guess);
  return d ? d.toLocaleDateString("en-US", LONG) : s;
}

// "5/17/2026" — for the short Date lines under the signatures. Always carries
// the year. Text that isn't a date is returned exactly as typed.
export function formatShortDate(
  raw: string,
  now: Date = new Date(),
  guess: YearGuess = "nearest"
): string {
  const s = (raw || "").trim();
  if (!s) return "";
  const d = parseTypedDate(s, now, guess);
  return d ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}` : s;
}
